import { useState } from 'react';
import { MapPin, Navigation, Keyboard, X } from 'lucide-react';
import { useGeolocation } from '../hooks/useGeolocation';

type GeolocationPromptProps = {
  onLocationGranted: (coords: { lat: number; lng: number }) => void;
  onLocationSkipped: () => void;
  onManualEntry: (text: string) => void;
};

export const GeolocationPrompt = ({
  onLocationGranted,
  onLocationSkipped,
  onManualEntry,
}: GeolocationPromptProps) => {
  const { isLoading, requestLocation } = useGeolocation();
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualText, setManualText] = useState('');

  const handleAllow = () => {
    requestLocation();

    // Listen for the result via the geolocation API directly
    // since the hook state won't update in this handler synchronously
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onLocationGranted({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        // Permission denied or error — show manual entry
        setShowManualInput(true);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleManualSubmit = () => {
    if (manualText.trim()) {
      onManualEntry(manualText.trim());
    }
  };

  if (showManualInput) {
    return (
      <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-xl p-5 text-center">
        <Keyboard className="w-8 h-8 text-sy-teal mx-auto mb-3" />
        <p className="text-white text-sm mb-3">
          Enter your suburb or postcode
        </p>
        <input
          type="text"
          value={manualText}
          onChange={(e) => setManualText(e.target.value)}
          placeholder="e.g. Willowbank QLD or 4306"
          className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-sy-teal mb-3"
          onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
        />
        <div className="flex gap-2">
          <button
            onClick={handleManualSubmit}
            disabled={!manualText.trim()}
            className="flex-1 py-2.5 rounded-lg bg-sy-teal text-sy-navy font-medium text-sm disabled:opacity-40"
          >
            Submit
          </button>
          <button
            onClick={onLocationSkipped}
            className="px-4 py-2.5 rounded-lg border border-white/20 text-white/60 text-sm hover:text-white"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm bg-white/5 border border-white/10 rounded-xl p-5 text-center">
      <MapPin className="w-8 h-8 text-sy-teal mx-auto mb-3" />
      <p className="text-white font-medium mb-1">Help us locate you</p>
      <p className="text-white/50 text-sm mb-4">
        Sharing your location helps us find nearest emergency services if needed.
      </p>

      <button
        onClick={handleAllow}
        disabled={isLoading}
        className="w-full min-h-[48px] rounded-lg bg-sy-teal text-sy-navy font-medium text-base flex items-center justify-center gap-2 hover:bg-sy-teal/90 transition-colors disabled:opacity-60 mb-3"
      >
        <Navigation className="w-5 h-5" />
        {isLoading ? 'Getting Location...' : 'Allow Location'}
      </button>

      <button
        onClick={() => setShowManualInput(true)}
        className="w-full py-2.5 text-white/60 text-sm hover:text-white transition-colors mb-1"
      >
        Enter Location Manually
      </button>

      <button
        onClick={onLocationSkipped}
        className="w-full py-2 text-white/40 text-xs hover:text-white/60 transition-colors flex items-center justify-center gap-1"
      >
        <X className="w-3 h-3" />
        Skip for Now
      </button>
    </div>
  );
};
